import React from 'react';
import { cn } from '../../utils/cn.ts';
import { Icon } from './Icon.tsx';

interface SelectOption {
    label: string;
    value: string | number;
    disabled?: boolean;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    label?: string;
    options?: SelectOption[];  // Now optional - can use children instead
    error?: string;
    fullWidth?: boolean;
    helperText?: string;
    leftIcon?: string;
    inputSize?: 'sm' | 'md' | 'lg';
    children?: React.ReactNode;  // Support for <option> children
    ref?: React.Ref<HTMLSelectElement>;
}

const NativeSelect = 'select';

export const Select: React.FC<SelectProps> = ({
    label,
    options,
    error,
    fullWidth = true,
    helperText,
    leftIcon,
    inputSize = 'md',
    className,
    disabled,
    id,
    children,
    ref,
    ...props
}) => {
    const generatedId = React.useId();
    const selectId = id ?? generatedId;

    return (
        <div className={cn(fullWidth && 'w-full', className)}>
            {label ? <label 
                    htmlFor={selectId}
                    className="mb-1 block font-medium text-gray-700 text-sm dark:text-gray-300"
                >
                    {label}
                </label> : null}
            
            <div className="relative">
                {leftIcon ? <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name={leftIcon} size="sm" className="text-gray-400" />
                    </div> : null}

                <NativeSelect
                    ref={ref}
                    id={selectId}
                    disabled={disabled}
                    className={cn(
                        'block w-full appearance-none rounded-lg border',
                        'bg-white dark:bg-surface-dark',
                        'text-gray-900 dark:text-white',
                        'focus-ring transition-colors',
                        leftIcon ? 'pl-9' : 'pl-3',
                        'pr-10',
                        inputSize === 'sm' ? 'py-1 text-xs' : inputSize === 'lg' ? 'py-3 text-base' : 'py-2 text-sm',
                        error 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 hover:border-gray-400 dark:border-border-dark dark:hover:border-gray-600',
                        disabled && 'cursor-not-allowed bg-gray-50 opacity-60 dark:bg-gray-800'
                    )}
                    {...props}
                >
                    {options ? options.map((opt) => (
                        <option 
                            key={opt.value} 
                            value={opt.value} 
                            disabled={opt.disabled}
                            className="bg-white text-gray-900 dark:bg-surface-dark dark:text-gray-100"
                        >
                            {opt.label}
                        </option>
                    )) : children}
                </NativeSelect>

                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <Icon name="expand_more" size="sm" className="text-gray-400" />
                </div>
            </div>

            {(error || helperText) ? <p className={cn('mt-1 text-xs', error ? 'text-red-500' : 'text-gray-500 dark:text-gray-400')}>
                    {error || helperText}
                </p> : null}
        </div>
    );
};

Select.displayName = 'Select';














